import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, buttonClass, Card, EmptyState, PageTitle, TEXT_LINK } from '@rooming/ui';
import { formatDateRangeInZone, formatInZone } from '@rooming/web-core';
import { Link, useParams } from 'react-router';
import { backoffice } from '../api';
import { Loaded, STAY_STATUS } from '../common';
import { StayBoard } from './StayBoard';
import { HotelTermsEditor } from './PlacementPages';
import { GroupInvitation } from './GroupInvitation';
import { GuidePassengers } from './GuidePassengers';

// The reduced navigation of hotel users and coordinators (BACKOFFICE_DESIGN §2): "Mis estadías" and one stay —
// a coordinator reads the distribution and, on site, registers a passenger or restores a passenger's access
// (GuidePassengers); a hotel user has the single editable field "habitación del hotel".

const useExternalStays = () =>
  useQuery({ queryKey: ['external-stays'], queryFn: async () => (await backoffice.externalStays()).items });

export function ExternalStaysPage() {
  const stays = useExternalStays();
  return (
    <>
      <PageTitle subtitle="Las estadías a las que te dieron acceso.">Mis estadías</PageTitle>
      <Loaded query={stays}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState title="Todavía no tenés estadías asignadas">
              Cuando Funtour te dé acceso a una estadía o a un grupo, aparece acá.
            </EmptyState>
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2">
              {items.map((stay) => (
                <li key={stay.id}>
                  <Card className="flex h-full flex-col gap-2">
                    <h2 className="text-lg font-semibold break-words">{stay.group?.name ?? 'Grupo'}</h2>
                    <p className="text-base">
                      {stay.hotel?.name ?? 'Hotel'}
                      {stay.hotel === null ? '' : ` · ${stay.hotel.city}`}
                    </p>
                    <p className="text-base">
                      {formatDateRangeInZone(stay.checkInAt, stay.checkOutAt, stay.hotel?.timezone ?? 'UTC')}
                    </p>
                    <p>
                      <Badge tone={STAY_STATUS[stay.status]?.tone ?? 'neutral'}>
                        {STAY_STATUS[stay.status]?.text ?? stay.status}
                      </Badge>
                    </p>
                    <Link
                      to={`/estadias/${stay.id}`}
                      aria-label={`Ver la distribución de ${stay.group?.name ?? 'la estadía'}`}
                      className={buttonClass('primary', 'mt-auto')}
                    >
                      {stay.access === 'HOTEL_USER' ? 'Cargar habitaciones' : 'Ver distribución'}
                    </Link>
                  </Card>
                </li>
              ))}
            </ul>
          )
        }
      </Loaded>
    </>
  );
}

export function ExternalStayPage() {
  const { stayId = '' } = useParams();
  const stays = useExternalStays();
  return (
    <>
      <div className="mb-3">
        <Link to="/" className={TEXT_LINK}>
          ← Mis estadías
        </Link>
      </div>
      <Loaded query={stays}>
        {(items) => {
          const stay = items.find((s) => s.id === stayId);
          if (stay === undefined) return <Alert tone="error">No tenés acceso a esta estadía.</Alert>;
          const hotel = stay.access === 'HOTEL_USER';
          return (
            <>
              <PageTitle
                eyebrow={hotel ? 'Vista hotel' : 'Vista guía'}
                subtitle={`${stay.hotel?.name ?? 'Hotel'} · ${formatDateRangeInZone(stay.checkInAt, stay.checkOutAt, stay.hotel?.timezone ?? 'UTC')}`}
              >
                {stay.group?.name ?? 'Estadía'}
              </PageTitle>
              {!hotel && <GroupInvitation groupId={stay.groupId} />}
              {hotel && stay.hotelMessage !== null && stay.hotelMessage.text !== null && (
                <div className="mb-5">
                  <Alert tone="warning" title="Mensaje de Funtour">
                    <span className="block text-base whitespace-pre-line">{stay.hotelMessage.text}</span>
                    {stay.hotelMessage.updatedAt !== null && (
                      <span className="mt-2 block text-sm">
                        Enviado el {formatInZone(stay.hotelMessage.updatedAt, stay.hotel?.timezone ?? 'UTC')}.
                      </span>
                    )}
                  </Alert>
                </div>
              )}
              {hotel && (
                <div className="mb-5">
                  <HotelTermsEditor stayId={stay.id} initial={stay.hotelTerms} />
                </div>
              )}
              {hotel && (
                <div className="mb-5">
                  <Alert tone="info">
                    Cargá el número real de cada habitación y, si querés, los términos y condiciones de tu hotel. Es lo
                    único que podés modificar; queda registrado. Abajo tenés la lista de pasajeros para el check-in y la
                    descarga de la rooming list.
                  </Alert>
                </div>
              )}
              <StayBoard
                stayId={stay.id}
                mode={hotel ? 'hotel' : 'read'}
                showProgress={!hotel}
                canExport
                canSeeDocuments
                timeZone={stay.hotel?.timezone ?? 'UTC'}
              />
              {!hotel && (
                <GuidePassengers
                  groupId={stay.groupId}
                  defaultDocumentType={stay.hotel?.timezone === 'America/Asuncion' ? 'CI_PY' : 'RUT'}
                />
              )}
            </>
          );
        }}
      </Loaded>
    </>
  );
}
